import React, { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { get } from 'lodash';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useRecaptcha } from 'hooks/useRecaptcha/useRecaptcha';
import { useGetNextPageUrl } from 'hooks/useGetNextPage/useGetNextPageUrl';
import { useGetProduct } from 'hooks/useGetProduct/useGetProduct';
import { useBeginCheckoutEvent } from 'hooks/GTMAnalytics/useGTMAnalytics';
import { useSendEventToSegment } from 'hooks/useSendEventToSegment/useSendEventToSegment';
import { EmailInputForm } from 'components/emailInputForm/EmailInputForm';
import { useExistingUserCheck } from 'hooks/useExistingUserCheck/useExistingUserCheck';
import { useContentViewed } from 'hooks/Analytics/useAnalytics';
import { LoadingOverlay } from 'components/loading/LoadingOverlay';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import {
  ANALYTICS_ID,
  COOKIE_AJS_ANONYMOUS_ID,
  COOKIE_AJS_USER_ID,
  COOKIE_MYOB_VISITOR_ID,
  EXCLUDED_PRODUCTS,
  FLOW_STEPS,
  GTM_ANALYTICS_CONSTANTS,
  PRICING_LIST_URL,
  REGIONS,
  SUPPORTED_ANNUAL_PRICE_PRODUCTS
} from '../../constants';
import config from '../../Config';

const isAnnualPricingSupported = (region: string, productId: string): boolean => {
  const supportedAnnualPricingProducts = get(SUPPORTED_ANNUAL_PRICE_PRODUCTS, region);
  return supportedAnnualPricingProducts?.includes(productId);
};

const isProductDeprecated = (region: string, productId: string): boolean => {
  const excludedProductsByRegion = get(EXCLUDED_PRODUCTS, region);
  return excludedProductsByRegion && Object.values(excludedProductsByRegion).includes(productId);
};

export const EmailPage: React.FC = () => {
  const { pathname } = useLocation();
  const [emailValue, setEmailValue] = useState('');
  const {
    productId,
    region,
    setHasPurchaseSubmitted,
    setEmailChecked
  } = useSessionContext();
  setHasPurchaseSubmitted(false);
  const { isLoading: isProductLoading, isSuccess: fetchProductDataSuccess, productData } = useGetProduct();
  const { loginAfterLogout } = useAuth0();
  const {
    isNewUser, isEmailChecked, isError, identityId, checkedEmail
  } = useExistingUserCheck(emailValue);

  const {
    isLoading: isRecaptchaLoading,
    success: isRecaptchaSuccess
  } = useRecaptcha({ enabled: isEmailChecked, action: 'view', identityId });

  const redirectUrl = useGetNextPageUrl(FLOW_STEPS.EMAIL)

  const appState = () => ({ clientInformation: { email: checkedEmail, isNewUser } });

  const redirect = () => {
    if (isNewUser) {
      loginAfterLogout({
        appState: appState(),
        authorizationParams: {
          screen_hint: 'signup',
          ui_hint: 'essentials',
          login_hint: checkedEmail,
          redirect_uri: `${config.BUY_BASE_URL}${redirectUrl}`
        }
      });
    } else {
      loginAfterLogout({
        appState: appState(),
        authorizationParams: {
          login_hint: checkedEmail,
          redirect_uri: `${config.BUY_BASE_URL}${redirectUrl}`
        }
      });
    }
  }

  useEffect(() => {
    if (isEmailChecked && isRecaptchaSuccess) {
      redirect()
    }
  }, [isEmailChecked, isRecaptchaSuccess]);

  if (isRecaptchaSuccess === false) {
    return <Redirect to='/error' />;
  }

  const isLoading = isRecaptchaLoading || isEmailChecked;

  if (isProductLoading) {
    return (<div data-testid='email-page-loading' />);
  }
  if (isProductDeprecated(region, productId)) {
    const redirectUrl = region === REGIONS.AUSTRALIA ? PRICING_LIST_URL.AU : PRICING_LIST_URL.NZ;
    window.location.replace(redirectUrl);
    return null;
  }

  setEmailChecked && setEmailChecked(isEmailChecked);
  trackCheckoutStarting(isEmailChecked, emailValue);

  const isProductAndRegionMatched: boolean = fetchProductDataSuccess && productData && region === productData.region;
  const isAnnualRoute = pathname.includes('annual');
  const rejectAnnualPrice: boolean = isAnnualRoute && !isAnnualPricingSupported(region, productId);

  if (
    !isProductAndRegionMatched ||
    rejectAnnualPrice
  ) {
    return <Redirect to='/error' />;
  }

  return (
    <>
      <LoadingOverlay isLoading={isLoading} />
      <EmailInputForm
        onSubmit={setEmailValue}
        isError={isError}
      />
    </>
  );
};

export const trackCheckoutStarting = (isEmailChecked: boolean, emailValue: string): void => {
  /* eslint-disable react-hooks/rules-of-hooks */
  useContentViewed({
    elementId: ANALYTICS_ID.CONTENT_VIEWED_TRIAL_FORM,
    enabled: isEmailChecked
  });

  const myobVisitorId = Cookies.get(COOKIE_MYOB_VISITOR_ID);
  const anonymousId = Cookies.get(COOKIE_AJS_ANONYMOUS_ID);
  const userId = Cookies.get(COOKIE_AJS_USER_ID);
  const identifyParams = {
    userId,
    anonymousId,
    traits: {
      email: emailValue,
      myobVisitorId
    }
  };

  const trackParams = {
    event: GTM_ANALYTICS_CONSTANTS.BEGIN_CHECKOUT_SEGMENT,
    userId,
    anonymousId,
    properties: {
      myobVisitorId
    }
  };
  useBeginCheckoutEvent({ email: emailValue }, isEmailChecked, GTM_ANALYTICS_CONSTANTS.BEGIN_CHECKOUT_SEGMENT);

  useSendEventToSegment({ shouldSendSegmentEvent: isEmailChecked, identifyParams, trackParams });
  /* eslint-enable react-hooks/rules-of-hooks */
};
